import React, { useRef, useState } from "react";
import styled from "styled-components";
import colors from "@/design/colors";
import fonts from "@/design/typography";
import { ChevronDown, ChevronUp } from "@/components/icons/chevron";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";

interface BasicDropdownProps {
  isOpen?: boolean;
  options: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }[];
  iconWidth?: string;
  iconHeight?: string;
  label?: string;
  customTrigger?: (open: boolean) => React.ReactNode;
}

const BasicDropdown = ({
  options,
  iconWidth = "20px",
  iconHeight = "20px",
  label,
  customTrigger,
}: BasicDropdownProps) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutsideCloseModal([dropdownRef], () => setOpen(false));

  return (
    <Wrapper ref={dropdownRef}>
      <TriggerButton onClick={() => setOpen((prev) => !prev)}>
        {customTrigger ? (
          customTrigger(open)
        ) : (
          <>
            <span>{label}</span>
            {open ? (
              <ChevronUp
                width={iconWidth}
                height={iconHeight}
                color={colors.gray900}
              />
            ) : (
              <ChevronDown
                width={iconWidth}
                height={iconHeight}
                color={colors.gray900}
              />
            )}
          </>
        )}
      </TriggerButton>

      {open && (
        <Menu>
          {options.map((opt, idx) => (
            <MenuItem
              key={idx}
              onClick={() => {
                opt.onClick();
                setOpen(false);
              }}
            >
              {opt.icon && <IconWrapper>{opt.icon}</IconWrapper>}
              {opt.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

export default BasicDropdown;

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: ${colors.gray900};
  font-size: ${fonts.body3SemiBold.fontSize};
  font-family: ${fonts.body3SemiBold.fontFamily};
  line-height: ${fonts.body3SemiBold.lineHeight};
`;

const Menu = styled.ul`
  position: absolute;
  top: 130%;
  right: 0;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  z-index: 100;
  min-width: 140px;
`;

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  color: ${colors.gray700};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${colors.gray100};
  }

  font-size: ${fonts.label4Regular.fontSize};
  font-family: ${fonts.label4Regular.fontFamily};
  line-height: ${fonts.label4Regular.lineHeight};
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
